import { catchError, map, retry } from 'rxjs/operators';
import { Upa, UaSummaryRead, DisplayStatusUpa } from '../uas/ua.model';
import { UpaTranslation } from '../uas/ua-building-blocks';
import { ApiService } from '@hub-services/api';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Observable, of } from 'rxjs';
import { UpasByStatusCount } from '../hub-store/ua-config/upas-by-status-count.model';

@Injectable({ providedIn: 'root' })
export class UpasService {
  path: string = '/api';
  constructor(private apiService: ApiService) { }

  countUpasByCategory(upas: Upa[]) {

    const counts: Record<string, number> = {};

    // Typed Upa[], but keep the runtime guard until strictNullChecks lands — it isn't enforced yet,
    // so an untyped/rehydrated caller could still pass null/undefined; guard instead of throwing.
    upas?.forEach((upa: Upa) => {
      upa.category_ids?.forEach((categoryId: string) => {
        counts[categoryId] = counts.hasOwnProperty(categoryId) ? counts[categoryId] + 1 : 1;
      })
    })

    return counts;
  }

  countUpasByStatus(airlineId: number): Observable<UpasByStatusCount[]> {
    return this.apiService
      .get<{ data: UpasByStatusCount[] }>(`${this.path}/v2/airlines/${airlineId}/summary_upas_number`)
      .pipe(
        catchError(() => of({ data: [] })),
        map(response => response.data)
      )
  }

  getGroupCountByStatus(items: UpasByStatusCount[], categories?: {id: string, name: string}[]) {
    let upaCountByStatus = {
      draft: 0,
      pending: 0,
      published: 0,
      disabled: 0,
    };
    items?.forEach(item => {
      if(!categories || !!categories.find(category => item.token === category.id)) {
        upaCountByStatus.draft += item.upas.draft;
        upaCountByStatus.pending += item.upas.in_review;
        upaCountByStatus.published += item.upas.published;
        upaCountByStatus.disabled += item.upas.disabled;
      }
    });

    return upaCountByStatus;
  }

  // `/summary_upas` returns the v1 UPA summary variant (is_* booleans, no `status` string) — not the
  // v2 `Upa`. Left `any[]` pending a dedicated view model; `displayStatus` is a FE-only view label
  // derived from the booleans, kept OFF the domain `status` field (no cast into the domain model).
  getUpasByStatus(airlineId: string, status: string): Observable<DisplayStatusUpa[]> {
    return this.apiService
      .get<UaSummaryRead[]>(`${this.path}/airlines/${airlineId}/summary_upas?status=${status}`)
      .pipe(
        // Explicit ingestion -> enriched transform: stamp the derived FE status label.
        map(upas => upas.map((upa): DisplayStatusUpa => ({
          ...upa,
          displayStatus: this.getStatus(upa)
        }))),
        catchError(() => of([])),
      );
  }
  
  getAllUpas(airlineId: string): Observable<Upa[]> {
    return this.apiService
      .get<{ data: Upa[] }>(`${this.path}/v2/airlines/${airlineId}/upas`)
      .pipe(
        retry(1),
        catchError(() => of({ data: [] })),
        map(response => response.data),
      );
  }

  processUpaTranslations(upa: Upa, languages: string[]) {
    const defaultTranslation: UpaTranslation = {
      headline: '',
      description: '',
      description_md: '',
      cta_text: '',
      cta_url: '',
      translated: false,
    };
    languages.forEach((code: string) => {
      if (!upa.translations[code]) {
        upa.translations[code] = defaultTranslation;
      }
    });
    return upa;
  }

  processUpasTranslations(upas: Upa[], languages: string[], defaultCode: string) {
    return [...upas].map((upa: Upa) => {
      const defaultTranslation = _.clone(upa.translations[defaultCode]);
      languages.forEach((code: string) => {
        // translations is Partial — guard before dereferencing (missing locale ~ empty translation).
        const translation = upa.translations[code];
        if (!translation || translation.headline.length === 0) {
          // Previously reused the single `defaultTranslation` object (with a shared mutation of
          // `.translated`) across every fallback language, and would have crashed here if the
          // upa had no translation at all for `defaultCode`. Spread into a per-language copy and
          // fall through to `undefined` in that case instead.
          upa.translations[code] = defaultTranslation ? { ...defaultTranslation, translated: false } : defaultTranslation;
        } else {
          // upa.translations[code].translated = true;
        }
      });
      return { ...upa };
    });
  }

  private getStatus(upa: UaSummaryRead): string {
    if(upa.is_draft) {
      return 'Draft';
    } else if (upa.in_review) {
      return 'In review';
    } else if (upa.is_published) {
      return 'Published';
    } else {
      return 'Disabled';
    }
  }
}
